import { useSignal } from "@preact/signals";
import type { LoaderArgs, PageArgs } from "bunfx";
import { RedirectError } from "bunfx";
import { useEffect, useRef } from "preact/hooks";
import {
  IcoPencil,
  IcoPlus,
  IcoSpinner,
  IcoStar,
  IcoTrash,
} from "../../components/icons";
import {
  BrandedLayout,
  Button,
  ConfirmDialog,
  useFocusTrap,
} from "../../components/ui";
import { usePageTitle } from "../../hooks";
import { rpc } from "../../rpc";
import { showError } from "../../stores/errors";
import type { MimicState } from "../../stores/mimic";
import type { BrandColor, CoreValue, User } from "../../types";
import { isAdmin } from "../../types";
import { getErrorMessage } from "../../utils/errors";

type CoreValuesPageState = {
  currentUser: User;
  mimic: MimicState;
  orgId: string;
  orgName: string;
  brandColor: BrandColor;
  isOrgAdmin: boolean;
  hasReports: boolean;
  values: CoreValue[];
};

export async function load(args: LoaderArgs): Promise<CoreValuesPageState> {
  const orgId = args.params.orgId ?? "";

  // Stage 1: Auth first to ensure clean /login redirect on 401
  const [authResult, orgsResult] = await Promise.all([
    rpc.auth.me({}),
    rpc.orgs.list({}),
  ]);

  if (!authResult.user) {
    throw new RedirectError("/login");
  }

  // Stage 2: Data calls after auth validated
  const [orgResult, valuesResult] = await Promise.all([
    rpc.orgs.get({ id: orgId }),
    rpc.coreValues.list({ workspaceId: orgId }),
  ]);

  const currentUser = authResult.user as User;
  const currentMember = orgResult.currentMember;
  const isOrgAdmin = isAdmin(currentMember.role);

  const orgFromList = orgsResult.organizations.find((o) => o.id === orgId);
  const brandColor = (orgFromList?.brandColor ?? "indigo") as BrandColor;

  return {
    currentUser,
    mimic: authResult.mimic ?? null,
    orgId,
    orgName: orgResult.organization.name,
    brandColor,
    isOrgAdmin,
    hasReports: orgFromList?.hasReports ?? false,
    values: valuesResult.values,
  };
}

export function Page({ state }: PageArgs<typeof load>) {
  usePageTitle("Values");
  const {
    currentUser,
    mimic,
    orgId,
    orgName,
    brandColor,
    isOrgAdmin,
    hasReports,
  } = state.value;

  const values = useSignal<CoreValue[]>(state.value.values);
  const showCreateModal = useSignal(false);
  const editingValue = useSignal<CoreValue | null>(null);
  const deletingValue = useSignal<CoreValue | null>(null);

  // Sync values when state changes (client-side navigation reuse)
  useEffect(() => {
    values.value = state.value.values;
  }, [state.value.values]);

  function handleCreated(v: CoreValue) {
    values.value = [...values.value, v];
  }

  function handleUpdated(v: CoreValue) {
    values.value = values.value.map((existing) =>
      existing.id === v.id ? v : existing,
    );
  }

  async function handleDelete(v: CoreValue) {
    try {
      await rpc.coreValues.remove({ id: v.id });
      values.value = values.value.filter((existing) => existing.id !== v.id);
      deletingValue.value = null;
    } catch (err) {
      showError(getErrorMessage(err));
    }
  }

  return (
    <BrandedLayout
      user={currentUser}
      mimic={mimic}
      orgId={orgId}
      orgName={orgName}
      brandColor={brandColor}
      activeTab="values"
      isAdmin={isOrgAdmin}
      hasReports={hasReports}
    >
      <div class="flex flex-col gap-6">
        {/* Header */}
        <div class="flex items-start justify-between gap-4">
          <div>
            <h2 class="text-lg font-semibold text-text">Core Values</h2>
            <p class="text-sm text-text-muted mt-1">
              {values.value.length > 0
                ? "The principles that guide our team."
                : isOrgAdmin
                  ? "Define the values that guide your organization."
                  : "Your organization's core values will appear here."}
            </p>
          </div>
          <div class="flex items-center gap-2 flex-shrink-0">
            {values.value.length > 0 && (
              <a
                href={`/orgs/${orgId}/values/digest`}
                class="px-3 py-2 rounded-lg border border-border text-sm font-medium text-text-muted hover:text-text hover:bg-surface-hover transition-colors"
              >
                View Digest
              </a>
            )}
            {isOrgAdmin && values.value.length > 0 && (
              <Button
                variant="primary"
                onClick={() => {
                  showCreateModal.value = true;
                }}
              >
                <IcoPlus class="size-4" />
                Add Value
              </Button>
            )}
          </div>
        </div>

        {/* Values list */}
        {values.value.length === 0 ? (
          <EmptyState
            isAdmin={isOrgAdmin}
            onAdd={() => {
              showCreateModal.value = true;
            }}
          />
        ) : (
          <div class="flex flex-col gap-4">
            {values.value.map((v, index) => (
              <ValueCard
                key={v.id}
                value={v}
                number={index + 1}
                isAdmin={isOrgAdmin}
                onEdit={() => {
                  editingValue.value = v;
                }}
                onDelete={() => {
                  deletingValue.value = v;
                }}
              />
            ))}
          </div>
        )}
      </div>

      {/* Create modal */}
      {showCreateModal.value && (
        <ValueFormModal
          orgId={orgId}
          onClose={() => {
            showCreateModal.value = false;
          }}
          onSaved={handleCreated}
        />
      )}

      {/* Edit modal */}
      {editingValue.value && (
        <ValueFormModal
          orgId={orgId}
          existing={editingValue.value}
          onClose={() => {
            editingValue.value = null;
          }}
          onSaved={handleUpdated}
        />
      )}

      {/* Delete confirmation */}
      <ConfirmDialog
        open={deletingValue.value !== null}
        title="Delete Value"
        confirmLabel="Delete"
        onConfirm={() => {
          if (deletingValue.value) handleDelete(deletingValue.value);
        }}
        onCancel={() => {
          deletingValue.value = null;
        }}
      >
        Are you sure you want to delete "{deletingValue.value?.name}"? This
        cannot be undone.
      </ConfirmDialog>
    </BrandedLayout>
  );
}

// ============================================================================
// Empty State
// ============================================================================

function EmptyState({
  isAdmin,
  onAdd,
}: {
  isAdmin: boolean;
  onAdd: () => void;
}) {
  return (
    <div class="bg-surface border border-border rounded-xl p-12 text-center">
      <div class="inline-flex items-center justify-center size-14 rounded-full bg-surface-muted mb-4">
        <IcoStar class="size-7 text-text-muted" />
      </div>
      <h3 class="text-base font-semibold text-text mb-2">
        No core values defined yet
      </h3>
      <p class="text-sm text-text-muted max-w-sm mx-auto mb-6">
        {isAdmin
          ? "Add your organization's core values so everyone knows what your team stands for."
          : "Your admin hasn't added core values yet. Check back later!"}
      </p>
      {isAdmin && (
        <Button variant="primary" onClick={onAdd}>
          <IcoPlus class="size-4" />
          Add Your First Value
        </Button>
      )}
    </div>
  );
}

// ============================================================================
// Value Card
// ============================================================================

function ValueCard({
  value,
  number,
  isAdmin,
  onEdit,
  onDelete,
}: {
  value: CoreValue;
  number: number;
  isAdmin: boolean;
  onEdit: () => void;
  onDelete: () => void;
}) {
  return (
    <div class="bg-surface border border-border rounded-xl p-5 flex gap-4">
      {/* Number badge */}
      <div class="flex-shrink-0 size-10 rounded-full bg-[var(--brand-100)] text-[var(--brand-700)] flex items-center justify-center font-bold text-base">
        {number}
      </div>

      {/* Content */}
      <div class="flex-1 min-w-0">
        <div class="flex items-start justify-between gap-3">
          <div class="min-w-0">
            <h3 class="text-base font-semibold text-text">{value.name}</h3>
            {value.tagline && (
              <p class="text-sm text-[var(--brand-600)] font-medium italic mt-0.5">
                "{value.tagline}"
              </p>
            )}
          </div>
          {isAdmin && (
            <div class="flex items-center gap-1 flex-shrink-0">
              <button
                type="button"
                onClick={onEdit}
                class="p-1.5 rounded-md text-text-muted hover:text-text hover:bg-surface-hover transition-colors"
                aria-label={`Edit ${value.name}`}
              >
                <IcoPencil class="size-4" />
              </button>
              <button
                type="button"
                onClick={onDelete}
                class="p-1.5 rounded-md text-text-muted hover:text-red-600 hover:bg-red-50 transition-colors"
                aria-label={`Delete ${value.name}`}
              >
                <IcoTrash class="size-4" />
              </button>
            </div>
          )}
        </div>
        {value.description && (
          <p class="text-sm text-text-muted mt-2 leading-relaxed">
            {value.description}
          </p>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// Value Form Modal (Create + Edit)
// ============================================================================

function ValueFormModal({
  orgId,
  existing,
  onClose,
  onSaved,
}: {
  orgId: string;
  existing?: CoreValue;
  onClose: () => void;
  onSaved: (value: CoreValue) => void;
}) {
  const name = useSignal(existing?.name ?? "");
  const tagline = useSignal(existing?.tagline ?? "");
  const description = useSignal(existing?.description ?? "");
  const isSaving = useSignal(false);
  const nameInputRef = useRef<HTMLInputElement>(null);

  const isEditing = !!existing;
  const canSave = name.value.trim().length > 0 && !isSaving.value;

  useEffect(() => {
    if (nameInputRef.current) {
      nameInputRef.current.focus();
      if (isEditing) {
        nameInputRef.current.select();
      }
    }
  }, []);

  const dialogRef = useFocusTrap<HTMLDivElement>({
    open: true,
    onEscape: onClose,
  });

  async function handleSave() {
    if (!canSave) return;
    isSaving.value = true;
    try {
      if (isEditing) {
        const result = await rpc.coreValues.update({
          id: existing.id,
          name: name.value.trim(),
          tagline: tagline.value.trim(),
          description: description.value.trim(),
        });
        onSaved(result.value);
      } else {
        const result = await rpc.coreValues.create({
          workspaceId: orgId,
          name: name.value.trim(),
          tagline: tagline.value.trim(),
          description: description.value.trim(),
        });
        onSaved(result.value);
      }
      onClose();
    } catch (err) {
      showError(getErrorMessage(err));
      isSaving.value = false;
    }
  }

  function handleKeyDown(e: KeyboardEvent) {
    if (e.key === "Enter" && !e.shiftKey && canSave) {
      // Only submit on Enter if focus is not in the textarea
      const target = e.target as HTMLElement;
      if (target.tagName !== "TEXTAREA") {
        e.preventDefault();
        handleSave();
      }
    }
  }

  return (
    <div
      class="fixed inset-0 z-[60] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="value-form-title"
    >
      {/* Backdrop */}
      <div
        class="absolute inset-0 bg-black/50 transition-opacity"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Dialog */}
      <div
        ref={dialogRef}
        class="relative w-full max-w-lg bg-surface rounded-xl shadow-xl animate-scale-in"
      >
        <div class="p-6">
          <h3
            id="value-form-title"
            class="text-lg font-semibold text-text mb-4"
          >
            {isEditing ? "Edit Core Value" : "Add Core Value"}
          </h3>

          <div class="flex flex-col gap-4" onKeyDown={handleKeyDown}>
            {/* Name */}
            <div>
              <label class="block text-sm font-medium text-text mb-1">
                Name <span class="text-red-500">*</span>
                <input
                  ref={nameInputRef}
                  type="text"
                  value={name.value}
                  onInput={(e) => {
                    name.value = (e.target as HTMLInputElement).value;
                  }}
                  placeholder="e.g. Impact and Innovation"
                  maxLength={255}
                  class="mt-1 w-full px-3 py-2 rounded-lg border border-border bg-surface text-text placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-text-subtle font-normal"
                  disabled={isSaving.value}
                />
              </label>
            </div>

            {/* Tagline */}
            <div>
              <label class="block text-sm font-medium text-text mb-1">
                Tagline
                <input
                  type="text"
                  value={tagline.value}
                  onInput={(e) => {
                    tagline.value = (e.target as HTMLInputElement).value;
                  }}
                  placeholder='e.g. "Do stuff that matters."'
                  maxLength={500}
                  class="mt-1 w-full px-3 py-2 rounded-lg border border-border bg-surface text-text placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-text-subtle font-normal"
                  disabled={isSaving.value}
                />
              </label>
              <p class="text-xs text-text-muted mt-1">
                A short memorable phrase that captures the value.
              </p>
            </div>

            {/* Description */}
            <div>
              <label class="block text-sm font-medium text-text mb-1">
                Description
                <textarea
                  value={description.value}
                  onInput={(e) => {
                    description.value = (e.target as HTMLTextAreaElement).value;
                  }}
                  placeholder="Explain what this value means for your team..."
                  rows={4}
                  maxLength={5000}
                  class="mt-1 w-full px-3 py-2 rounded-lg border border-border bg-surface text-text placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-text-subtle resize-none font-normal"
                  disabled={isSaving.value}
                />
              </label>
            </div>
          </div>
        </div>

        <div class="flex gap-3 px-6 pb-6">
          <button
            type="button"
            onClick={handleSave}
            disabled={!canSave}
            class="flex-1 px-4 py-2 rounded-lg bg-[var(--brand-600)] hover:bg-[var(--brand-700)] text-white text-sm font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {isSaving.value && <IcoSpinner class="size-4 animate-spin" />}
            {isEditing ? "Save Changes" : "Add Value"}
          </button>
          <button
            type="button"
            onClick={onClose}
            disabled={isSaving.value}
            class="flex-1 px-4 py-2 rounded-lg text-sm font-medium text-text-muted hover:bg-surface-hover transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
